import { resolveKeyUri, resolveUrl } from "./resolve-url.js";
import { MEDIA_PLAYLIST_METADATA_KEY, getMediaPlaylistMetadata, isResolvedTrack } from "../types/index.js";
import { matchTag, parseByteRange, parseExtInfDuration } from "./parse-attributes.js";
import { isUndefined } from "@videojs/utils/predicate";
//#region src/media/hls/parse-media-playlist.ts
/**
* MPEG-2 Transport Stream (IANA `video/MP2T`, lowercased for `isTypeSupported`). Video + audio TS — there is no
* `audio/mp2t`.
*/
const MPEG_TS_MIME = "video/mp2t";
/** Raw ADTS AAC packed-audio (HLS `.aac` segments; IANA `audio/aac`). */
const RAW_AAC_MIME = "audio/aac";
const CONTAINER_MIME_BY_EXTENSION = {
	".ts": MPEG_TS_MIME,
	".aac": RAW_AAC_MIME
};
/** The non-fMP4 container MIMEs the parser detects — all currently treated as unplayable. */
const NON_FMP4_CONTAINER_MIMES = new Set(Object.values(CONTAINER_MIME_BY_EXTENSION));
/**
* Non-fMP4 container MIME for a (resolved, absolute) segment URL, by file extension, ignoring the query string.
* `undefined` for fMP4 / unrecognized.
*/
function containerMimeFromSegment(url) {
	if (!url) return void 0;
	let path;
	try {
		path = new URL(url).pathname.toLowerCase();
	} catch {
		path = url.toLowerCase().split("?")[0] ?? "";
	}
	const dot = path.lastIndexOf(".");
	return dot === -1 ? void 0 : CONTAINER_MIME_BY_EXTENSION[path.slice(dot)];
}
/**
* Position a freshly-parsed window (whose segment `startTime`s are snapshot- local, i.e. from 0) onto the timeline
* established by the previous resolved snapshot. Carries the timeline forward using the media-sequence overlap and the
* previous window's _actual_ segment durations — see `internal/design/spf/live-presentation-timeline-model.md`.
*
* - **Overlap** (`0 <= offset < previous.segments.length`): the new window's first segment is the same segment as
*   `previous.segments[offset]`; anchor to its start (URLs checked — a mismatch warns).
* - **Sequence went backwards** (non-conformant): reset to the local base.
* - **Full turnover** (no overlap): estimate forward from the previous window's end across the unseen gap. This is the
*   _only_ place EXT-X-TARGETDURATION is used for timing — an upper-bound estimate, since the actual rolled-off
*   durations are gone (exact recovery is the deferred PDT decision).
*
* Returns the rebased segments (the window edge is `segments[0].startTime`, derived — never stored on the track).
*/
function placeOnPreviousTimeline(previous, segments, mediaSequence, targetDuration) {
	const prevSegments = previous.segments;
	const localBase = segments[0]?.startTime ?? 0;
	if (prevSegments.length === 0 || segments.length === 0) return segments;
	const offset = mediaSequence - (getMediaPlaylistMetadata(previous)?.mediaSequence ?? 0);
	let anchor;
	if (offset >= 0 && offset < prevSegments.length) {
		const overlap = prevSegments[offset];
		if (overlap.url !== segments[0].url) console.warn(`[parseMediaPlaylist] media-sequence aligns previous[${offset}] with the new window's first segment, but URLs differ (${overlap.url} vs ${segments[0].url}); sequence numbers may be unreliable.`);
		anchor = overlap.startTime;
	} else if (offset < 0) {
		console.warn(`[parseMediaPlaylist] media-sequence went backwards (offset ${offset}); resetting timeline.`);
		anchor = localBase;
	} else {
		const last = prevSegments[prevSegments.length - 1];
		const newFirst = segments[0];
		if (last.startDate !== void 0 && newFirst.startDate !== void 0) anchor = last.startTime + (newFirst.startDate - last.startDate);
		else {
			anchor = last.startTime + last.duration + (offset - prevSegments.length) * targetDuration;
			console.warn(`[parseMediaPlaylist] full window turnover (offset ${offset} >= ${prevSegments.length}); no PDT to bridge — estimating from previous end.`);
		}
	}
	const shift = anchor - localBase;
	return shift === 0 ? segments : segments.map((segment) => ({
		...segment,
		startTime: segment.startTime + shift
	}));
}
/**
* Place a window on the frozen wall-clock anchor — the track's `startDate` (wall clock at media-time 0). The anchoring
* PDT-bearing segment lands at `segment.startDate − anchor`, so the window sits on the shared presentation timeline
* rather than a local-from-zero one (the `startDate` recomputed afterwards then reads back as the anchor — idempotent).
* This is the **main live path on every parse**: first resolves place against the pre-stamped shell anchor, and reloads
* re-place from PDT so drift can't accumulate and a long-stall turnover re-places with no overlap bridging (the HLS
* authoring spec's §8.1 EXTINF-accuracy rule bounds the per-reload correction to ~one video frame). Falls back to the
* local base when no segment carries PDT — there's nothing to anchor against (callers guard this for reloads, where the
* local base would reset the timeline).
*/
function placeOnAnchor(segments, anchor) {
	const anchorSegment = segments.find((segment) => !isUndefined(segment.startDate));
	if (!anchorSegment || isUndefined(anchorSegment.startDate)) return segments;
	const shift = anchorSegment.startDate - anchor - anchorSegment.startTime;
	if (shift === 0) return segments;
	return segments.map((segment) => ({
		...segment,
		startTime: segment.startTime + shift
	}));
}
function parseMediaPlaylist(text, previous, config = {}) {
	const resolveKey = config.resolveKeyUri ?? resolveKeyUri;
	const lines = text.split(/\r?\n/);
	const baseUrl = previous.url;
	const segments = [];
	let initSegmentUrl;
	let initSegmentByteRange;
	let currentDuration = 0;
	let currentByteRange;
	let currentTime = 0;
	let segmentIndex = 0;
	let previousByteRangeEnd;
	let currentStartDate;
	let targetDuration = 0;
	let mediaSequence = 0;
	let playlistType;
	let endList = false;
	let holdBack;
	let lowLatency = false;
	const keys = [];
	const seenKeys = /* @__PURE__ */ new Set();
	for (const line of lines) {
		const trimmed = line.trim();
		if (!trimmed || trimmed.startsWith("#") && !trimmed.startsWith("#EXT")) continue;
		if (trimmed.startsWith("#EXT-X-TARGETDURATION:")) {
			targetDuration = Number.parseInt(trimmed.slice(22), 10) || 0;
			continue;
		}
		if (trimmed.startsWith("#EXT-X-MEDIA-SEQUENCE:")) {
			mediaSequence = Number.parseInt(trimmed.slice(22), 10) || 0;
			continue;
		}
		if (trimmed.startsWith("#EXT-X-PROGRAM-DATE-TIME:")) {
			const parsed = Date.parse(trimmed.slice(25).trim());
			currentStartDate = Number.isNaN(parsed) ? currentStartDate : parsed / 1e3;
			continue;
		}
		if (trimmed.startsWith("#EXT-X-PLAYLIST-TYPE:")) {
			const value = trimmed.slice(21).trim();
			playlistType = value === "VOD" || value === "EVENT" ? value : void 0;
			continue;
		}
		const key = matchTag(trimmed, "EXT-X-KEY");
		if (key) {
			const method = key.get("METHOD");
			if (method !== void 0 && method !== "NONE") {
				const uri = key.get("URI");
				const keyFormat = key.get("KEYFORMAT");
				const keyId = key.get("KEYID");
				const iv = key.get("IV");
				const identity = [
					method,
					uri,
					keyFormat,
					keyId,
					iv
				].join("\0");
				if (!seenKeys.has(identity)) {
					seenKeys.add(identity);
					keys.push({
						method,
						...uri !== void 0 && { uri: resolveKey(uri, baseUrl) },
						...keyFormat !== void 0 && { keyFormat },
						...keyId !== void 0 && { keyId },
						...iv !== void 0 && { iv }
					});
				}
			}
			continue;
		}
		const serverControl = matchTag(trimmed, "EXT-X-SERVER-CONTROL");
		if (serverControl) {
			const value = serverControl.get("HOLD-BACK");
			if (value !== void 0) {
				const parsed = Number.parseFloat(value);
				if (Number.isFinite(parsed) && parsed > 0) holdBack = parsed;
			}
			if (serverControl.get("PART-HOLD-BACK") !== void 0) lowLatency = true;
			continue;
		}
		if (trimmed.startsWith("#EXT-X-PART-INF") || trimmed.startsWith("#EXT-X-PART:")) {
			lowLatency = true;
			continue;
		}
		if (trimmed === "#EXTM3U" || trimmed.startsWith("#EXT-X-VERSION:") || trimmed.startsWith("#EXT-X-INDEPENDENT-SEGMENTS")) continue;
		const mapAttrs = matchTag(trimmed, "EXT-X-MAP");
		if (mapAttrs) {
			const uri = mapAttrs.get("URI");
			if (uri) {
				initSegmentUrl = resolveUrl(uri, baseUrl);
				const byteRangeStr = mapAttrs.get("BYTERANGE");
				if (byteRangeStr) initSegmentByteRange = parseByteRange(byteRangeStr, 0) ?? void 0;
			}
			continue;
		}
		if (trimmed.startsWith("#EXTINF:")) {
			currentDuration = parseExtInfDuration(trimmed.slice(8));
			continue;
		}
		if (trimmed.startsWith("#EXT-X-BYTERANGE:")) {
			currentByteRange = parseByteRange(trimmed.slice(17), previousByteRangeEnd) ?? void 0;
			continue;
		}
		if (trimmed === "#EXT-X-ENDLIST") {
			endList = true;
			continue;
		}
		if (!trimmed.startsWith("#") && currentDuration > 0) {
			const segment = {
				id: `segment-${mediaSequence + segmentIndex}`,
				url: resolveUrl(trimmed, baseUrl),
				duration: currentDuration,
				startTime: currentTime
			};
			if (!isUndefined(currentStartDate)) {
				segment.startDate = currentStartDate;
				currentStartDate += currentDuration;
			}
			if (currentByteRange) {
				segment.byteRange = currentByteRange;
				previousByteRangeEnd = currentByteRange.end + 1;
			} else previousByteRangeEnd = void 0;
			segments.push(segment);
			currentTime += currentDuration;
			segmentIndex++;
			currentDuration = 0;
			currentByteRange = void 0;
		}
	}
	const complete = endList || playlistType === "VOD";
	const anchor = previous.startDate;
	const hasPdt = segments.some((segment) => !isUndefined(segment.startDate));
	const placed = !isUndefined(anchor) && hasPdt ? placeOnAnchor(segments, anchor) : isResolvedTrack(previous) ? placeOnPreviousTimeline(previous, segments, mediaSequence, targetDuration) : segments;
	const lastPlaced = placed[placed.length - 1];
	const placedEnd = lastPlaced ? lastPlaced.startTime + lastPlaced.duration : 0;
	const trackDuration = complete ? placedEnd : Number.POSITIVE_INFINITY;
	const anchorSegment = placed.find((segment) => !isUndefined(segment.startDate));
	const startDate = anchorSegment && !isUndefined(anchorSegment.startDate) ? anchorSegment.startDate - anchorSegment.startTime : void 0;
	const initialization = previous.type === "text" && !initSegmentUrl ? void 0 : initSegmentUrl ? {
		url: initSegmentUrl,
		...initSegmentByteRange ? { byteRange: initSegmentByteRange } : {}
	} : { url: "" };
	const detectedContainer = initSegmentUrl ? void 0 : containerMimeFromSegment(placed[0]?.url);
	const mimeType = previous.type !== "text" && detectedContainer ? detectedContainer : previous.mimeType;
	return {
		...previous,
		mimeType,
		startTime: 0,
		startDate,
		duration: trackDuration,
		segments: placed,
		initialization,
		metadata: {
			...previous.metadata,
			[MEDIA_PLAYLIST_METADATA_KEY]: {
				targetDuration,
				mediaSequence,
				playlistType,
				lowLatency,
				endList,
				holdBack,
				encrypted: keys.length > 0,
				...keys.length > 0 && { keys }
			}
		}
	};
}
//#endregion
export { MPEG_TS_MIME, NON_FMP4_CONTAINER_MIMES, RAW_AAC_MIME, parseMediaPlaylist };

//# sourceMappingURL=parse-media-playlist.js.map